import cn from 'classnames';
import type { ReactNode } from 'react';

import styles from './radioselect.module.scss';
import { useId } from 'react';

export type RadioSelectOption<T extends string> = {
    value: T;
    label: ReactNode;
    onHover?: () => void;
    bodyComponent?: ReactNode;
};

export interface RadioSelectProps<T extends string> {
    options: Array<RadioSelectOption<T>>;
    value: T;
    onChange: (value: T) => void;
    name?: string;
    keepMountedBodyComponents?: boolean;
}

export function RadioSelect<T extends string>(props: RadioSelectProps<T>) {
    const fallbackName = useId();
    const selectedOption = props.options.find((option) => option.value === props.value);

    return (
        <>
            <div className={styles.selectContainer}>
                {props.options.map((option) => {
                    const isSelected = option.value === props.value;

                    const labelClassName = cn(styles.label, {
                        [styles.selected]: isSelected,
                    });

                    const inputId = `${fallbackName}-${option.value}`;

                    return (
                        <label
                            className={labelClassName}
                            key={option.value}
                            htmlFor={inputId}
                            onMouseOver={option.onHover}
                            onFocus={option.onHover}
                        >
                            {option.label}
                            <input
                                id={inputId}
                                type="radio"
                                name={props.name || fallbackName}
                                onChange={() => props.onChange(option.value)}
                                checked={isSelected}
                            />
                        </label>
                    );
                })}
            </div>

            {props.keepMountedBodyComponents ? (
                props.options.some((option) => option.bodyComponent) ? (
                    <div className={styles.bodyComponentContainer}>
                        {props.options.map((option) => {
                            const isSelected = option.value === props.value;

                            return option.bodyComponent ? (
                                <div key={option.value} hidden={!isSelected} aria-hidden={!isSelected}>
                                    {option.bodyComponent}
                                </div>
                            ) : null;
                        })}
                    </div>
                ) : null
            ) : selectedOption?.bodyComponent ? (
                <div className={styles.bodyComponentContainer}>{selectedOption.bodyComponent}</div>
            ) : null}
        </>
    );
}
